import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Divider, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TextField, MenuItem, Chip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { productionPlansApi, commitmentsApi } from '../../../api/production.api';
import { extractApiError } from '../../../api/error';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../hooks/useAuth';

interface Props {
  planId: number | null;
  onClose: () => void;
  onChanged: () => void;
}

export function ProductionPlanDetailDialog({ planId, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = planId !== null;
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [commitItemId, setCommitItemId] = useState<number | ''>('');
  const [commitDate, setCommitDate] = useState('');
  const [commitQty, setCommitQty] = useState('');

  const { data: plan } = useQuery({ queryKey: ['production-plan', planId], queryFn: () => productionPlansApi.get(planId as number), enabled: open });

  const refresh = () => { setError(null); qc.invalidateQueries({ queryKey: ['production-plan', planId] }); onChanged(); };
  const onErr = (e: unknown) => setError(extractApiError(e));

  const submitMut = useMutation({ mutationFn: () => productionPlansApi.submit(planId as number), onSuccess: refresh, onError: onErr });
  const approveMut = useMutation({ mutationFn: () => productionPlansApi.approve(planId as number), onSuccess: refresh, onError: onErr });
  const rejectMut = useMutation({ mutationFn: () => productionPlansApi.reject(planId as number, rejectReason), onSuccess: refresh, onError: onErr });
  const commitMut = useMutation({
    mutationFn: () => {
      const item = (plan?.items ?? []).find((i) => i.id === commitItemId);
      return commitmentsApi.create({ planId: planId as number, planItemId: Number(commitItemId), productId: item?.productId ?? 0, commitDate, committedQty: Number(commitQty) });
    },
    onSuccess: () => { setCommitItemId(''); setCommitDate(''); setCommitQty(''); refresh(); },
    onError: onErr,
  });
  const produceMut = useMutation({ mutationFn: ({ id, qty }: { id: number; qty: number }) => commitmentsApi.produce(id, qty), onSuccess: refresh, onError: onErr });

  if (!plan) return null;
  const canCommit = ['approved', 'active'].includes(plan.status) && hasPermission('production_commitment.create');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <span>{plan.planCode} — {plan.title}</span>
          <StatusBadge status={plan.status} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {plan.rejectionReason && <Alert severity="warning" sx={{ mb: 2 }}>Rejected: {plan.rejectionReason}</Alert>}

        <Divider textAlign="left" sx={{ mb: 1 }}>Planned products</Divider>
        <Table size="small">
          <TableHead><TableRow><TableCell>Product</TableCell><TableCell align="right">Planned</TableCell><TableCell align="right">Produced</TableCell></TableRow></TableHead>
          <TableBody>
            {(plan.items ?? []).map((i) => (
              <TableRow key={i.id}><TableCell>#{i.productId}</TableCell><TableCell align="right">{i.plannedQty}</TableCell><TableCell align="right">{i.producedQty}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider textAlign="left" sx={{ my: 1 }}>Daily commitments</Divider>
        {canCommit && (
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Plan item" value={commitItemId} onChange={(e) => setCommitItemId(Number(e.target.value))} sx={{ minWidth: 160 }}>
              {(plan.items ?? []).map((i) => <MenuItem key={i.id} value={i.id}>#{i.productId}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Date" type="date" InputLabelProps={{ shrink: true }} value={commitDate} onChange={(e) => setCommitDate(e.target.value)} />
            <TextField size="small" label="Committed Qty" type="number" value={commitQty} onChange={(e) => setCommitQty(e.target.value)} sx={{ width: 130 }} />
            <Button size="small" variant="outlined" disabled={!commitItemId || !commitDate || !commitQty || commitMut.isPending} onClick={() => commitMut.mutate()}>Commit</Button>
          </Stack>
        )}
        <Table size="small">
          <TableHead><TableRow><TableCell>Date</TableCell><TableCell align="right">Committed</TableCell><TableCell align="right">Produced</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {(plan.commitments ?? []).map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.commitDate}</TableCell>
                <TableCell align="right">{c.committedQty}</TableCell>
                <TableCell align="right">{c.producedQty}</TableCell>
                <TableCell><Chip size="small" label={c.status} color={c.status === 'produced' ? 'success' : 'default'} /></TableCell>
                <TableCell align="right">
                  {c.status !== 'produced' && hasPermission('production_commitment.update') && (
                    <Button size="small" onClick={() => produceMut.mutate({ id: c.id, qty: c.committedQty })}>Mark Produced</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(plan.commitments ?? []).length === 0 && <TableRow><TableCell colSpan={5}>No commitments yet.</TableCell></TableRow>}
          </TableBody>
        </Table>

        {plan.status === 'pending_approval' && hasPermission('production_plan.approve') && (
          <TextField size="small" label="Rejection reason (required to reject)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} fullWidth sx={{ mt: 2 }} />
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {(plan.status === 'draft' || plan.status === 'rejected') && hasPermission('production_plan.submit') && (
          <Button variant="contained" onClick={() => submitMut.mutate()} disabled={submitMut.isPending}>Submit for Approval</Button>
        )}
        {plan.status === 'pending_approval' && hasPermission('production_plan.approve') && (
          <>
            <Button color="success" variant="contained" onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>Approve</Button>
            <Button color="error" variant="outlined" disabled={!rejectReason || rejectMut.isPending} onClick={() => rejectMut.mutate()}>Reject</Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
